import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  Box, Typography, Card, CardContent, Grid, TextField, Button, Switch,
  FormControlLabel, Alert, CircularProgress, Stack, Divider, Chip,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import {
  EMAIL_SETTINGS_QUERY,
  SAVE_EMAIL_SETTINGS_MUTATION,
  TEST_EMAIL_SETTINGS_MUTATION,
} from '../graphql/queries';
import { useCompanyDetails } from '../utils/companyDetails';
import WhatsAppSettingsCard from '../components/WhatsAppSettingsCard';

const EMPTY_SETTINGS = {
  smtpHost: '',
  smtpPort: 587,
  smtpSecure: false,
  smtpUser: '',
  smtpPassword: '',
  fromName: '', // empty = use the company's display name (Admin > Company Details)
  fromEmail: '',
  replyToEmail: '',
};

export default function AdminEmailSettingsPage() {
  const company = useCompanyDetails();
  const [form, setForm] = useState(EMPTY_SETTINGS);
  const [testEmail, setTestEmail] = useState('');
  const [snack, setSnack] = useState({ open: false, msg: '', ok: true });
  const [loaded, setLoaded] = useState(false);

  const { loading } = useQuery(EMAIL_SETTINGS_QUERY, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      if (data?.emailSettings) {
        setForm({
          smtpHost: data.emailSettings.smtpHost || '',
          smtpPort: data.emailSettings.smtpPort || 587,
          smtpSecure: data.emailSettings.smtpSecure || false,
          smtpUser: data.emailSettings.smtpUser || '',
          smtpPassword: data.emailSettings.smtpPassword || '',
          fromName: data.emailSettings.fromName || '',
          fromEmail: data.emailSettings.fromEmail || '',
          replyToEmail: data.emailSettings.replyToEmail || '',
        });
      }
      setLoaded(true);
    },
  });

  const [saveSettings, { loading: saving }] = useMutation(SAVE_EMAIL_SETTINGS_MUTATION, {
    onCompleted: () => setSnack({ open: true, msg: 'Email settings saved successfully!', ok: true }),
    onError: (e) => setSnack({ open: true, msg: e.message, ok: false }),
  });

  const [testSettings, { loading: testing }] = useMutation(TEST_EMAIL_SETTINGS_MUTATION, {
    onCompleted: (data) => {
      const result = data.testEmailSettings;
      if (result.success) {
        setSnack({ open: true, msg: 'Test email sent successfully!', ok: true });
      } else {
        setSnack({ open: true, msg: `Failed: ${result.error}`, ok: false });
      }
    },
    onError: (e) => setSnack({ open: true, msg: e.message, ok: false }),
  });

  const handleChange = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    if (!form.smtpHost || !form.smtpUser || !form.smtpPassword || !form.fromEmail) {
      setSnack({ open: true, msg: 'Please fill in all required fields', ok: false });
      return;
    }
    saveSettings({ variables: { ...form, smtpPort: parseInt(form.smtpPort, 10) } });
  };

  const handleTest = () => {
    if (!testEmail.trim()) {
      setSnack({ open: true, msg: 'Please enter a test recipient email', ok: false });
      return;
    }
    testSettings({ variables: { recipientEmail: testEmail } });
  };

  if (loading && !loaded) return <Box p={3}><CircularProgress /></Box>;

  return (
    <Box p={3}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Email Settings</Typography>
          <Typography variant="body2" color="text.secondary">
            Configure SMTP settings for sending emails from the application
          </Typography>
        </Box>
        <Chip label="Admin" color="primary" />
      </Stack>

      {snack.open && (
        <Alert
          severity={snack.ok ? 'success' : 'error'}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          icon={snack.ok ? <CheckCircleIcon /> : <ErrorIcon />}
          sx={{ mb: 2 }}
        >
          {snack.msg}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>SMTP Configuration</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth label="SMTP Host *" value={form.smtpHost}
                onChange={handleChange('smtpHost')} placeholder="smtp.gmail.com"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth label="SMTP Port *" type="number" value={form.smtpPort}
                onChange={handleChange('smtpPort')} placeholder="587"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControlLabel
                control={<Switch checked={form.smtpSecure} onChange={handleChange('smtpSecure')} />}
                label="Use SSL/TLS"
                sx={{ mt: 1 }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth label="SMTP Username *" value={form.smtpUser}
                onChange={handleChange('smtpUser')} placeholder="your-email@gmail.com"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth label="SMTP Password *" type="password" value={form.smtpPassword}
                onChange={handleChange('smtpPassword')} placeholder="App password or SMTP password"
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom>Sender Information</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth label="From Name" value={form.fromName}
                onChange={handleChange('fromName')} placeholder={company.brandName}
                helperText={`Leave empty to use the company display name ("${company.brandName}"), which follows Company Details`}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth label="From Email *" value={form.fromEmail}
                onChange={handleChange('fromEmail')} placeholder="noreply@matrimonyapp.com"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth label="Reply-To Email" value={form.replyToEmail}
                onChange={handleChange('replyToEmail')} placeholder="support@matrimonyapp.com"
              />
            </Grid>
          </Grid>

          <Box mt={3}>
            <Button
              variant="contained" startIcon={<SaveIcon />}
              onClick={handleSave} disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Settings'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>Send Test Email</Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Verify your SMTP settings by sending a test email
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              label="Recipient Email" value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder="test@example.com" sx={{ minWidth: 300 }}
            />
            <Button
              variant="outlined" startIcon={<SendIcon />}
              onClick={handleTest} disabled={testing}
            >
              {testing ? 'Sending...' : 'Send Test'}
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {/* WhatsApp Business (wedding invitations) */}
      <WhatsAppSettingsCard />
    </Box>
  );
}
